import { useState } from 'react';
import ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { useMemoizedFn } from 'ahooks';

import { getTrueDataFeedbackHistoryNew } from '@intelligence/services/topcharts';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { rowStyle } from '@intelligence/components/DownloadCommon/DownloadPage/common/baseConfig';

import {
  buildTrueDataSheets,
  normalizeTrueDataDownloadResponse,
} from '../utils/trueData';

import type { IRegion } from '@intelligence/types/common';
import type {
  ITrueDataHeaderLabels,
  IPublicFeedbackParams,
  ISortConfig,
  TTrueDataMode,
  TTrueDataSourceLabels,
} from '../types';

interface IUseTrueDataDownloadParams {
  mode: TTrueDataMode;
  sortConfig: ISortConfig;
  headerLabels: ITrueDataHeaderLabels;
  sourceTypeLabels: TTrueDataSourceLabels;
  formatMessage: IFormatMessage;
  feedBackParams?: IPublicFeedbackParams;
  gameName?: string;
  gameId?: string;
  regionsCountries?: IRegion[];
}

interface IUseTrueDataDownloadReturn {
  isDownloading: boolean;
  download: () => Promise<void>;
}

/**
 * 封装真数下载的请求和文件生成，组件层只负责触发并展示 loading。
 */
export default function useTrueDataDownload(
  params: IUseTrueDataDownloadParams,
): IUseTrueDataDownloadReturn {
  const {
    mode,
    sortConfig,
    headerLabels,
    sourceTypeLabels,
    formatMessage,
    feedBackParams,
    gameName,
    gameId,
    regionsCountries,
  } = params;
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useMemoizedFn(async (): Promise<void> => {
    /** 下载锁为 true 时表示已有文件生成任务，重复触发会生成重复文件。 */
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const downloadParams = {
        entity_type: feedBackParams?.entity_type,
        game_id: feedBackParams?.game_id ?? gameId,
        mode,
        page_id: feedBackParams?.page_id ?? '',
      };
      const response = await getTrueDataFeedbackHistoryNew(downloadParams, {
        params: { is_download: true },
      });
      const downloadData = normalizeTrueDataDownloadResponse(response);
      const rows = (downloadData?.list ?? []).slice(0, MAX_ROWS_LIMIT);
      const sheets = buildTrueDataSheets({
        mode,
        rows,
        feedBackParams: downloadParams,
        sortConfig,
        headerLabels,
        sourceTypeLabels,
        formatMessage,
        gameName,
        regionsCountries,
      });
      const workbook = new ExcelJS.Workbook();

      Object.values(sheets).forEach((sheetConfig) => {
        const sheet = workbook.addWorksheet(sheetConfig.sheetName);
        sheetConfig.data?.forEach((row, rowIndex) => {
          const worksheetRow = sheet.addRow(row);
          // 第一行由下载配置生成表头；数据行保持默认样式。
          if (rowIndex === 0) {
            worksheetRow.eachCell({ includeEmpty: false }, (cell) => {
              Object.assign(cell, rowStyle);
            });
          }
        });
      });

      const data = await workbook.xlsx.writeBuffer();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      FileSaver.saveAs(
        blob,
        `${gameName || gameId}_True_Data_${mode === 'alinea' ? 'Alinea_Analytics' : 'DataBrain'}.xlsx`,
      );
    } finally {
      setIsDownloading(false);
    }
  });

  return {
    isDownloading,
    download,
  };
}
